import { useMemo, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { Alert, AlertTitle } from "@mui/material";
import { Trans } from "react-i18next";

import Timezone from "@/lib/timezone/Timezone";
import { useHighchartsTimezoneDefaults } from "@/lib/timezone/use-highcharts-timezone-defaults";
import { useTimezoneQuery } from "@/features/shared/hooks/queries";
import { useTranslation } from "@/lib/i18n";
import { AdminErrorBoundary, Menu } from "@/features/admin/components";
import DashboardPublicationDialog from "@/features/admin/features/dashboards/dialogs/dashboard-publication-dialog";
import { useAdminDashboard } from "@/features/admin/hooks";

import * as Styled from "./styles";

const NotActiveMessage = () => {
  const { t } = useTranslation();
  return (
    <Alert variant="outlined" severity="warning">
      <AlertTitle>{t("common.warning")}</AlertTitle>
      <Trans i18nKey="admin.messages.dashboard_not_active">
        Het dashboard is niet actief. Activeer het dashboard via de{" "}
        <Link to="settings">dashboard instellingen</Link> om het dashboard zichtbaar te
        maken voor de eindgebruikers
      </Trans>
    </Alert>
  );
};

/**
 * Second-level admin shell for dashboard-scoped CMS routes.
 * Auth/role gate, Header, and the outer error boundary live in AdminHeaderLayout.
 */
const AdminDashboardLayout = () => {
  const dashboard = useAdminDashboard();
  const [publicationDialogOpen, setPublicationDialogOpen] = useState(false);

  // Apply the edited dashboard's Highcharts timezone/weekday defaults once for
  // the whole admin route, so any widget preview chart beneath it reads the same
  // timezone. Settings live in the DB (not on the Dashboard object), so fetch
  // them (cache shared with the time-settings screen and widget previews).
  const timezoneQuery = useTimezoneQuery(dashboard.id);
  const timezone = useMemo(
    () => Timezone.fromSettings(timezoneQuery.data),
    [timezoneQuery.data],
  );
  useHighchartsTimezoneDefaults(timezone);

  return (
    <Styled.Content data-theme="light">
      <Menu onOpenPublicationDialog={() => setPublicationDialogOpen(true)} />
      <Styled.PageContainer>
        {!dashboard.active && <NotActiveMessage />}
        <AdminErrorBoundary tags={{ component: "AdminDashboardLayout" }}>
          <Outlet />
        </AdminErrorBoundary>
      </Styled.PageContainer>
      <DashboardPublicationDialog
        open={publicationDialogOpen}
        onClose={() => setPublicationDialogOpen(false)}
      />
    </Styled.Content>
  );
};

export default AdminDashboardLayout;
